import output from "_src/engine/output";
import events from "_src/engine/events";
import workspace from "_src/workspace";
import config from "_src/config";

export default {

  removeSelectedVertices(object) {

    const frames = object.frames;
    let verticesRemoved = false;

    for (let frame of frames) {

      const model = frame.model;
      const chunks = model.chunks;

      for (let chunk of chunks) {

        const geometry = chunk.geometry;

        const selectionMask = geometry.getItemBuffer("a_selection_mask");

        if (selectionMask == null) {
          output.error(output.DEVELOPERS_ERROR, "Invalid geometry. Missing selection mask. Aborting erase operation.");
          return false;
        }

        const hasSeletedVertices = geometry.checkHasSelectedVertices();

        if (!hasSeletedVertices) {
          continue;
        }

        verticesRemoved = true;

        recalculateBuffers(chunk);

        workspace.dropChunkHash(object, frame, chunk);

      }

      frame.updateNumPolygons();
      frame.updateNumVertices();

    }

    return verticesRemoved;

  },

}

function markRemovedTriangles(geometry, triRemoveMask) {

  const indices = geometry.getItemBuffer("indices");
  const mask = geometry.getItemBuffer("a_selection_mask");

  let numDropped = 0;
  const stride = geometry.isPointCloud ? 1 : 3;
  for (let i = 0; i < indices.length; i++) {

    const triInd = Math.floor(i / stride);
    if (triRemoveMask[triInd] === 1) {
      continue;
    }

    triRemoveMask[triInd] = 0;

    const ind = indices[i];
    if (mask[ind] === 1) {
      triRemoveMask[triInd] = 1;
      numDropped += stride;
    }

  }

  geometry.setHasSelectedVertices(false);

  const newIndCount = indices.length - numDropped;

  return newIndCount;

}

function recalculateBuffers(chunk) {

  buildNewBuffersData(chunk);
  chunk.markAttributeDirty("a_position");
  chunk.markAttributeDirty("a_normal");
  chunk.markAttributeDirty("a_uv");
  chunk.markAttributeDirty("a_color");
  chunk.markAttributeDirty("a_selection_mask");

  if (!chunk.geometry.isPointCloud) {
    chunk.markAttributeDirty("indices");
  } else {
    chunk.markAttributeDirty("a_size");
  }

  events.emit("update_chunk_octree", chunk);
  events.emit("update_chunk_halfedges", chunk);
  workspace.setNeedsDupliVerticesUpdate(chunk, true);

}

function buildNewBuffersData(chunk) {

  const geometry = chunk.geometry;
  const triRemoveMask = [];

  const newIndCount = markRemovedTriangles(geometry, triRemoveMask);

  const oldIndices = geometry.getItemBuffer("indices");
  const oldPositions = geometry.getItemBuffer("a_position");
  const oldNormals = geometry.getItemBuffer("a_normal");
  const oldUVs = geometry.getItemBuffer("a_uv");
  const oldColors = geometry.getItemBuffer("a_color");
  const oldOriginalIndices = geometry.getItemBuffer("old_indices");
  const vertRemoveMask = geometry.getItemBuffer("a_selection_mask");

  if (oldOriginalIndices && oldOriginalIndices.length > 0) {
    output.error(output.DEVELOPERS_ERROR, "two erases on same chunk without save");
  }

  if (newIndCount > 0) {
    markLonelyVerticesToRemove(geometry, oldIndices, vertRemoveMask);
  }

  const oldSize = geometry.getItemBuffer("a_size");

  const offsets = convertToOffsets(vertRemoveMask);
  const numRemovedVertices = offsets[offsets.length - 1];

  const newVertCount = newIndCount > 0 ?
    geometry.getVerticesCount() - numRemovedVertices :
    0;

  geometry.generateNewBuffers(newVertCount, newIndCount);

  if (newIndCount > 0) {

    const positions = geometry.getItemBuffer("a_position");
    const normals = geometry.getItemBuffer("a_normal");
    const uvs = geometry.getItemBuffer("a_uv");
    const colors = geometry.getItemBuffer("a_color");
    const size = geometry.getItemBuffer("a_size");
    const indices = geometry.getItemBuffer("indices");
    if (!geometry.isPointCloud && !config.enableRendering) geometry.initOldIndices(vertRemoveMask);

    let ind = 0;
    const stride = geometry.isPointCloud ? 1 : 3;

    for (let i = 0; i < oldIndices.length; ++i) {

      const triInd = Math.floor(i / stride);

      if (triRemoveMask[triInd] === 0) {

        const oldInd = oldIndices[i];

        const offset = offsets[oldInd];
        const newInd = oldInd - offset;

        const ind3V = 3 * newInd;
        const ind3vO = 3 * oldInd;

        positions[ind3V]     = oldPositions[ind3vO];
        positions[ind3V + 1] = oldPositions[ind3vO + 1];
        positions[ind3V + 2] = oldPositions[ind3vO + 2];

        if (normals) {
          normals[ind3V]     = oldNormals[ind3vO];
          normals[ind3V + 1] = oldNormals[ind3vO + 1];
          normals[ind3V + 2] = oldNormals[ind3vO + 2];
        }

        if (uvs) {
          const ind2V    = 2 * newInd;
          const ind2vO   = 2 * oldInd;
          uvs[ind2V]     = oldUVs[ind2vO];
          uvs[ind2V + 1] = oldUVs[ind2vO + 1];
        }

        if (colors) {
          const ind4V       = 4 * newInd;
          const ind4vO      = 4 * oldInd;
          colors[ind4V]     = oldColors[ind4vO];
          colors[ind4V + 1] = oldColors[ind4vO + 1];
          colors[ind4V + 2] = oldColors[ind4vO + 2];
          colors[ind4V + 3] = oldColors[ind4vO + 3];
        }

        if (size) {
          size[newInd] = oldSize[oldInd];
        }

        indices[ind++] = newInd;

      }
    }

  } // if (newIndCount > 0)

}

function markLonelyVerticesToRemove(geometry, indices, mask) {
  if (!geometry.isPointCloud) {
    for (let i = 0; i < indices.length; i += 3) {
      const v1 = indices[i];
      const v2 = indices[i + 1];
      const v3 = indices[i + 2];

      const m1 = mask[v1];
      const m2 = mask[v2];
      const m3 = mask[v3];

      if (m1 == 1 || m2 == 1 || m3 == 1) {
        if (m1 === 0) {
          mask[v1] = 2;
        }
        if (m2 === 0) {
          mask[v2] = 2;
        }
        if (m3 === 0) {
          mask[v3] = 2;
        }
      }
    }

    for (let i = 0; i < indices.length; i += 3) {
      const v1 = indices[i];
      const v2 = indices[i + 1];
      const v3 = indices[i + 2];

      const m1 = mask[v1];
      const m2 = mask[v2];
      const m3 = mask[v3];

      if (m1 != 1 && m2 != 1 && m3 != 1) {
        if (m1 === 2) {
          mask[v1] = 0;
        }
        if (m2 === 2) {
          mask[v2] = 0;
        }
        if (m3 === 2) {
          mask[v3] = 0;
        }
      }
    }

    for (let i = 0; i < mask.length; ++i) {
      if (mask[i] === 2) {
        mask[i] = 1;
      }
    }
  }
}

function convertToOffsets(mask) {

  let offsets = [];
  let offset = 0;
  for (let i = 0; i < mask.length; ++i) {
    offset += mask[i];
    offsets[i] = offset;
  }

  return offsets;
}
